import { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Tooltip,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useDataProvider, useGetIdentity, useNotify, useRedirect } from 'react-admin';
import { IrmDataProvider } from '../../providers/types';
import { KEEP_DIALOG_SIZE } from '../../root/touchTheme';

export const DeleteUserButton = ({
    uid,
    email,
    role,
}: {
    uid: string;
    email: string;
    role?: string;
}) => {
    const [open, setOpen] = useState(false);
    const [typed, setTyped] = useState('');
    const dataProvider = useDataProvider<IrmDataProvider>();
    const notify = useNotify();
    const redirect = useRedirect();
    const { identity } = useGetIdentity();
    const isSelf = identity?.id !== undefined && String(identity.id) === uid;
    const isAdminTarget = role === 'admin';
    const blockedReason = isSelf
        ? 'You cannot delete your own account.'
        : isAdminTarget
            ? 'Demote this admin first (set role to User and save), then delete.'
            : '';
    const blocked = isSelf || isAdminTarget;

    const { mutate, isPending } = useMutation({
        mutationKey: ['admin-delete-user', uid],
        mutationFn: async () => dataProvider.adminDeleteUser(uid),
        onSuccess: () => {
            notify('User deleted.');
            setOpen(false);
            redirect('/admin/users');
        },
        onError: (e: any) =>
            notify(e?.message || 'Delete failed.', { type: 'error' }),
    });

    return (
        <>
            <Tooltip title={blockedReason}>
                <span>
                    <Button
                        color="error"
                        variant="outlined"
                        onClick={() => setOpen(true)}
                        disabled={blocked}
                    >
                        Delete user
                    </Button>
                </span>
            </Tooltip>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                className={KEEP_DIALOG_SIZE}
            >
                <DialogTitle>Delete user?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This permanently removes the Firebase Auth account
                        and Firestore records. Type the user's email
                        (<strong>{email}</strong>) to confirm.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        fullWidth
                        value={typed}
                        onChange={e => setTyped(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={typed.trim() !== email || isPending}
                        onClick={() => mutate()}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
